import { Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import type { MatrixVendor, VendorBasicInfo } from "@/types/product-request";

import { useCreateRoom, useMatrixClient, useRoomMessages, useSendMessage } from "@/api/matrix-chat-query";
import { useRespondToRequest } from "@/api/product-request";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/auth";

interface ChatInterfaceProps {
	productRequestId: string;
	vendorId: string;
	vendor: VendorBasicInfo & MatrixVendor;
}

export function ChatInterface({ vendorId, vendor }: ChatInterfaceProps) {
	const [newMessage, setNewMessage] = useState("");
	const [roomId, setRoomId] = useState<string | null>(null);

	const user = useAuthStore(state => state.user);
	const scrollAreaRef = useRef<HTMLDivElement>(null);

	const respondToRequest = useRespondToRequest();

	console.log(roomId, "roomId");

	// Initialize Matrix client
	const matrixClient = useMatrixClient(
		user?.matrixHomeServer || "",
		user?.matrixAccessToken || "",
		user?.matrixUserId || ""
	);

	console.log(matrixClient, "matrixClient");

	// Create room mutation
	const createRoomMutation = useCreateRoom(matrixClient);

	// Get room messages
	const { data: roomMessages } = useRoomMessages(
		matrixClient!,
		roomId ?? "",
		{
			limit: 50,
			dir: "b"
		}
	);

	// Send message mutation
	const sendMessageMutation = useSendMessage(matrixClient);

	// Initialize chat room
	useEffect(() => {
		console.log(vendor, "vendor");
		if (user && vendor?.matrixUserId && !roomId) {
			createRoomMutation.mutateAsync(
				{
					invite: [vendor.matrixUserId],
					invite_name: `Chat with ${vendor.fullName || "Vendor"}`
				},
				{
					onSuccess: (data) => {
						if (!data)
							throw new Error("Failed to create chat room. Please try again.");
						console.log(data, "data");
						setRoomId(data.room_id);
					},
					onError: error => toast.error(error.message || "Failed to create chat room. Please try again.")
				}
			).then(console.log);
		}
	}, [user, vendor, roomId]);

	// Transform Matrix messages to our Message format
	const messages = roomMessages?.chunk.map(msg => ({
		id: msg.event_id,
		text: msg.content.body,
		senderId: msg.sender,
		timestamp: new Date(msg.origin_server_ts).toISOString()
	}));

	// Scroll to bottom when new messages arrive
	useEffect(() => {
		if (scrollAreaRef.current) {
			scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
		}
	}, [messages]);

	const handleSendMessage = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!newMessage.trim() || !roomId)
			return;

		try {
			await sendMessageMutation.mutateAsync({
				roomId,
				message: newMessage
			});
			setNewMessage("");
		}
		catch (error: any) {
			toast.error(error.message || "Failed to send message. Please try again.");
		}
	};

	if (!user)
		return null;

	// if (isLoading || createRoomMutation.isPending) {
	// 	return <ChatInterfaceSkeleton />;
	// }

	return (
		<div className="flex flex-col flex-1 overflow-hidden h-full">
			{/* Messages Area */}
			<ScrollArea ref={scrollAreaRef} className="flex-1 p-2">
				<div className="space-y-4">
					{messages?.map(message => (
						<div
							key={message.id}
							className={cn(
								"flex gap-2 max-w-full",
								message.senderId === vendorId
									? "flex-row"
									: "flex-row-reverse self-end"
							)}
						>
							<div
								className={cn(
									"rounded-2xl p-3",
									message.senderId === vendorId
										? "bg-muted"
										: "bg-primary text-primary-foreground"
								)}
							>
								<p className="text-sm">{message.text}</p>
								<span className="text-[10px] opacity-70 mt-1 block">
									{new Date(message.timestamp).toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit"
									})}
								</span>
							</div>
						</div>
					))}
				</div>
			</ScrollArea>

			{/* Message Input */}
			<div className="p-4 border-t bg-background">
				<form onSubmit={handleSendMessage} className="flex gap-2">
					<Input
						value={newMessage}
						onChange={e => setNewMessage(e.target.value)}
						placeholder="Type your message..."
						className="flex-1"
						disabled={respondToRequest.isPending || !roomId || sendMessageMutation.isPending}
					/>
					<Button
						type="submit"
						size="icon"
						className="shrink-0"
						disabled={respondToRequest.isPending || !roomId || sendMessageMutation.isPending}
					>
						<Send className="size-4" />
						<span className="sr-only">Send message</span>
					</Button>
				</form>
			</div>
		</div>
	);
}

// function ChatInterfaceSkeleton() {
// 	return (
// 		<div className="flex flex-col flex-1 overflow-hidden">
// 			<ScrollArea className="flex-1 p-4">
// 				<div className="space-y-4">
// 					{[1, 2, 3].map(i => (
// 						<div
// 							key={i}
// 							className={cn(
// 								"flex gap-2 max-w-[80%]",
// 								i % 2 === 0 ? "flex-row" : "flex-row-reverse self-end"
// 							)}
// 						>
// 							<div className="space-y-2">
// 								<Skeleton className="h-16 w-64 rounded-2xl" />
// 								<Skeleton className="h-3 w-20" />
// 							</div>
// 						</div>
// 					))}
// 				</div>
// 			</ScrollArea>

// 			<div className="p-4 border-t bg-background">
// 				<div className="flex gap-2">
// 					<Skeleton className="h-10 flex-1" />
// 					<Skeleton className="h-10 w-10" />
// 				</div>
// 			</div>
// 		</div>
// 	);
// }
